import { FOREIGN_LINK_BORDER, FOREIGN_LINK_NODE_SHADOW, FOREIGN_LINK_SIZE, HEIRARCHICAL_LINK_COLOR, HEIRARCHICAL_LINK_WIDTH } from "../config";
import { TCanvasLink } from "../types/TCanvasLink";
import { TCanvasOptions } from "../types/TCanvasOptions";

// Dark theme link colors
const LINK_COLOR_DEFAULT = "#3A476B";
const LINK_COLOR_HIGHLIGHT = "#0099FF";
const LINK_COLOR_DIM = "#1E3A5A";
const LINK_COLOR_HIGHLIGHT_GREY = "#8794A8"; // Slightly darker grey for better balance

export default function drawLinks(
  link: TCanvasLink,
  ctx: CanvasRenderingContext2D,
  scale: number,
  canvasOptions: TCanvasOptions | null,
  anyHovered: boolean,
  highlightedNodeIds?: number[]
) {
  // Check if both source and target are highlighted for data product builder
  const isDataProductHighlighted = highlightedNodeIds && highlightedNodeIds.length > 0
    ? (typeof link.source !== "string" && typeof link.target !== "string" &&
       highlightedNodeIds.includes(link.source.id) && highlightedNodeIds.includes(link.target.id))
    : false;

  if (typeof link.source == "string"
    || typeof link.target == "string"
    || link.source.x == undefined
    || link.target.y == undefined
    || link.source.y == undefined
    || link.target.x == undefined
  ) return;
  if (link.source.node_type == "DEFAULT_DOMAIN" || link.target.node_type == "DEFAULT_DOMAIN") return
  if (link.link_type == "foreign") {
    if (canvasOptions?.searchText.trim() != "") {
      drawLine(ctx, link.source, link.target, LINK_COLOR_DIM, 0.3)
      drawLinkNode(link, ctx, scale, FOREIGN_LINK_SIZE, FOREIGN_LINK_BORDER, LINK_COLOR_DIM)
    } else {
      // Data product highlighting takes precedence
      if (highlightedNodeIds && highlightedNodeIds.length > 0) {
        if (isDataProductHighlighted) {
          const lineWidth = 0.9
          drawAnimatedLine(ctx, link.source, link.target, LINK_COLOR_HIGHLIGHT_GREY, lineWidth)
          drawLinkNode(link, ctx, scale, FOREIGN_LINK_SIZE, FOREIGN_LINK_BORDER, LINK_COLOR_HIGHLIGHT_GREY)
        } else {
          drawLine(ctx, link.source, link.target, LINK_COLOR_DIM, 0.3)
          drawLinkNode(link, ctx, scale, FOREIGN_LINK_SIZE, FOREIGN_LINK_BORDER, LINK_COLOR_DIM)
        }
      } else if (anyHovered) {
        if (link.hovered) {
          const lineWidth = 0.9 // Slightly thinner line for highlight
          // Use lighter grey color with subtle animated dashed line effect
          drawAnimatedLine(ctx, link.source, link.target, LINK_COLOR_HIGHLIGHT_GREY, lineWidth)
          drawLinkNode(link, ctx, scale, FOREIGN_LINK_SIZE, FOREIGN_LINK_BORDER, LINK_COLOR_HIGHLIGHT_GREY)
        } else {
          drawLine(ctx, link.source, link.target, LINK_COLOR_DIM, 0.3)
          drawLinkNode(link, ctx, scale, FOREIGN_LINK_SIZE, FOREIGN_LINK_BORDER, LINK_COLOR_DIM)
        }
      } else {
        // Default state
        if (link.accuracy == undefined) {
          drawLine(ctx, link.source, link.target, LINK_COLOR_DEFAULT, 0.5, [5, 2])
          drawLinkNode(link, ctx, scale, FOREIGN_LINK_SIZE, FOREIGN_LINK_BORDER, LINK_COLOR_DEFAULT)
        } else if (link.accuracy > 80) {
          drawLine(ctx, link.source, link.target, LINK_COLOR_DEFAULT, 0.5)
          drawLinkNode(link, ctx, scale, FOREIGN_LINK_SIZE, FOREIGN_LINK_BORDER, "#949494")
        } else {
           // Lower accuracy links
          drawLine(ctx, link.source, link.target, LINK_COLOR_DEFAULT, 0.5)
          drawLinkNode(link, ctx, scale, FOREIGN_LINK_SIZE, FOREIGN_LINK_BORDER, "#f9c014")
        }
      }
    }
  } else {
    drawHeirarchicalLink(link, ctx, anyHovered, highlightedNodeIds);
  }
}

function drawHeirarchicalLink(
  link: TCanvasLink,
  ctx: CanvasRenderingContext2D,
  anyHovered: boolean = false,
  highlightedNodeIds?: number[]
) {
  if (typeof link.source == "string"
    || typeof link.target == "string"
    || link.source.x == undefined
    || link.target.y == undefined
    || link.source.y == undefined
    || link.target.x == undefined
  ) return undefined
  
  // Check if both source and target are highlighted for data product builder
  const isDataProductHighlighted = highlightedNodeIds && highlightedNodeIds.length > 0
    ? (highlightedNodeIds.includes(link.source.id) && highlightedNodeIds.includes(link.target.id))
    : false;
  
  // Data product highlighting takes precedence
  if (highlightedNodeIds && highlightedNodeIds.length > 0) {
    if (!isDataProductHighlighted) {
      // Fade out non-highlighted links
      ctx.save();
      ctx.globalAlpha = 0.18;
      drawLine(ctx, link.source, link.target, LINK_COLOR_DEFAULT, HEIRARCHICAL_LINK_WIDTH)
      ctx.restore();
    } else {
      // Keep highlighted links visible
      drawLine(ctx, link.source, link.target, LINK_COLOR_DEFAULT, HEIRARCHICAL_LINK_WIDTH)
    }
  } else if (anyHovered && !link.hovered) {
    // Apply fading for non-hovered hierarchical links when something is being hovered
    ctx.save();
    ctx.globalAlpha = 0.18; // Same fading as nodes
    drawLine(ctx, link.source, link.target, LINK_COLOR_DEFAULT, HEIRARCHICAL_LINK_WIDTH)
    ctx.restore();
  } else {
    // Normal state - using theme color for hierarchical links
    drawLine(ctx, link.source, link.target, LINK_COLOR_DEFAULT, HEIRARCHICAL_LINK_WIDTH)
  }
}


function drawLinkNode(
  link: TCanvasLink,
  ctx: CanvasRenderingContext2D,
  scale: number,
  radius: number,
  border: number,
  color: string
) {
  if (typeof link.source == "string"
    || typeof link.target == "string"
    || link.source.x == undefined
    || link.target.y == undefined
    || link.source.y == undefined
    || link.target.x == undefined
  ) return undefined

  let middle = {
    x: (link.source.x + link.target.x) / 2,
    y: (link.source.y + link.target.y) / 2
  }
  
  // Pure Black + Ice Blue theme (Option 4)
  // Maximum contrast with dramatic ice blue glow on pure black base
  const baseFillColor = link.hovered ? "#1A1A1A" : "#000000"; // Pure black (slightly lighter on hover)
  const glowColor = link.hovered ? "#9AE6FF" : "#7DD3FC"; // Ice blue (brighter on hover)
  
  // Draw circle with pulsating glow animation
  // Using higher intensity (1.4x) and 4 glow layers for dramatic effect
  drawPulsatingCircle(ctx, middle, radius * 1.2, baseFillColor, glowColor, 1.4, 1.3, 4);
}

function drawPill(
  ctx: CanvasRenderingContext2D,
  center: { x?: number, y?: number },
  width: number,
  height: number,
  color: string,
  angle: number = 0
) {
  if (center.x == undefined || center.y == undefined) return;
  
  ctx.save(); // Save current state
  
  // Translate to center point and rotate
  ctx.translate(center.x, center.y);
  ctx.rotate(angle);
  
  ctx.fillStyle = color;
  ctx.beginPath();
  
  // Calculate pill dimensions (now drawing centered at origin)
  const x = -width / 2;
  const y = -height / 2;
  const radius = height / 2; // Radius for the rounded ends
  
  // Draw rounded rectangle (pill shape)
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.arc(x + width - radius, y + radius, radius, -Math.PI / 2, Math.PI / 2);
  ctx.lineTo(x + radius, y + height);
  ctx.arc(x + radius, y + radius, radius, Math.PI / 2, -Math.PI / 2);
  ctx.closePath();
  
  ctx.fill();
  
  ctx.restore(); // Restore original state
}

function drawDiamond(
  ctx: CanvasRenderingContext2D,
  center: { x?: number, y?: number },
  size: number,
  color: string,
) {
  if (center.x == undefined || center.y == undefined) return;
  
  ctx.fillStyle = color;
  ctx.beginPath();
  
  // Draw diamond (rotated square) - 4 points
  ctx.moveTo(center.x, center.y - size);           // Top point
  ctx.lineTo(center.x + size, center.y);           // Right point
  ctx.lineTo(center.x, center.y + size);           // Bottom point
  ctx.lineTo(center.x - size, center.y);           // Left point
  ctx.closePath();
  
  ctx.fill();
}


export function getDashedLine() {
  const dashAnimateTime = 10
  const st = 2
  const gapLen = 2
  const dashLen = 4
  const t = ((+new Date() - st) % dashAnimateTime) / dashAnimateTime;
  const lineDash = t < 0.5 ? [0, gapLen * t * 2, dashLen, gapLen * (1 - t * 2)] : [dashLen * (t - 0.5) * 2, gapLen, dashLen * (1 - (t - 0.5) * 2), 0];
  if (lineDash == undefined) return [dashLen, gapLen];
  return lineDash;
}

function drawLine(
  ctx: CanvasRenderingContext2D,
  source: { x?: number, y?: number },
  target: { x?: number, y?: number },
  color: string,
  width: number,
  dash?: [number, number]
) {
  if (source.x == undefined
    || target.y == undefined
    || source.y == undefined
    || target.x == undefined
  ) return;
  if (dash) {
    ctx.setLineDash(dash)
  }
  ctx.strokeStyle = color
  ctx.lineWidth = width
  ctx.beginPath()
  ctx.moveTo(source.x, source.y)
  ctx.lineTo(target.x, target.y)
  ctx.stroke()
  ctx.setLineDash([])
}

function drawAnimatedLine(
  ctx: CanvasRenderingContext2D,
  source: { x?: number, y?: number },
  target: { x?: number, y?: number },
  color: string,
  width: number
) {
  if (source.x == undefined
    || target.y == undefined
    || source.y == undefined
    || target.x == undefined
  ) return;
  
  // Create subtle animated dashed line effect
  const time = Date.now();
  const dashLength = 8; // Longer dashes for subtlety
  const gapLength = 6; // Longer gaps for subtlety
  const speed = 80; // Slower animation (higher number = slower)
  const offset = (time / speed) % (dashLength + gapLength);
  
  ctx.setLineDash([dashLength, gapLength]);
  ctx.lineDashOffset = -offset; // Negative for forward flow
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.beginPath();
  ctx.moveTo(source.x, source.y);
  ctx.lineTo(target.x, target.y);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.lineDashOffset = 0;
}

function drawCircle(
  ctx: CanvasRenderingContext2D,
  center: { x?: number, y?: number },
  radius: number,
  color: string,
) {
  if (center.x == undefined || center.y == undefined) return;
  ctx.fillStyle = color;
  ctx.beginPath()
  ctx.arc(center.x, center.y, radius, 0, 2 * Math.PI, false)
  ctx.fill()
}

function drawPulsatingCircle(
  ctx: CanvasRenderingContext2D,
  center: { x?: number, y?: number },
  radius: number,
  fillColor: string,
  glowColor: string,
  intensity: number = 1.0,
  speed: number = 1.5,
  layers: number = 3
) {
  if (center.x == undefined || center.y == undefined) return;
  
  // Calculate pulsating effect using sine wave
  // Creates a smooth breathing animation
  const time = Date.now() / 1000; // Convert to seconds
  const pulseFrequency = speed; // Speed of pulsation
  const pulseIntensity = Math.sin(time * pulseFrequency) * 0.5 + 0.5; // Oscillates between 0 and 1
  
  // Outer glow layers - variable number based on layers parameter
  const glowLayers = [];
  if (layers >= 4) {
    glowLayers.push({ blur: 25, alpha: 0.25 * pulseIntensity * intensity });
  }
  if (layers >= 3) {
    glowLayers.push({ blur: 18, alpha: 0.3 * pulseIntensity * intensity });
  }
  if (layers >= 2) {
    glowLayers.push({ blur: 12, alpha: 0.4 * pulseIntensity * intensity });
  }
  glowLayers.push({ blur: 6, alpha: 0.5 * pulseIntensity * intensity });
  
  ctx.save();
  
  // Draw glow layers (from outermost to innermost)
  glowLayers.forEach((layer) => {
    ctx.shadowColor = glowColor;
    ctx.shadowBlur = layer.blur;
    ctx.globalAlpha = layer.alpha;
    ctx.fillStyle = glowColor;
    ctx.beginPath();
    ctx.arc(center.x, center.y, radius, 0, 2 * Math.PI, false);
    ctx.fill();
  });
  
  // Reset shadow for the main circle
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.globalAlpha = 1;
  
  // Draw the main circle with dark fill
  ctx.fillStyle = fillColor;
  ctx.beginPath();
  ctx.arc(center.x, center.y, radius, 0, 2 * Math.PI, false);
  ctx.fill();
  
  // Add a subtle inner bright ring that also pulsates
  const innerRingAlpha = 0.4 + (pulseIntensity * 0.3);
  ctx.globalAlpha = innerRingAlpha;
  ctx.strokeStyle = glowColor;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(center.x, center.y, radius * 0.7, 0, 2 * Math.PI, false);
  ctx.stroke();
  
  ctx.restore();
}
